import { Box, Card, CardActions, CardContent, CardHeader, Divider, List, ListItem, ListItemAvatar, ListItemText, Typography, Button } from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import theme from "src/theme";
import { formatDate } from "@arelia-utils/helpers/dates";
import { formatCurrency } from "@arelia-utils/helpers/formatCurrency";
import { PaymentHistory, SubscriptionPlanName } from "@arelia-types/subscriptions";
import SpinnerWrapper from "@arelia-components/ui-atoms/SpinnerWrapper";
import { http } from "../../api/http";
import { toast } from "sonner";
import { useSubscriptionManageUrl } from "@arelia-components/SubscriptionDetails/useSubscriptionDetailsAPI";
import { useLocation } from "react-router-dom";

type Props = {
  data: PaymentHistory[] | undefined;
  isLoading: boolean;
  isFreePlan: boolean;
}

export default function PaymentHistoryPresenter({ data, isLoading, isFreePlan }: Props) {
  const location = useLocation();
  const isInHQ = location.pathname.includes("/hq/");

  const getInvoiceUrl = async (invoiceId: string) => {
    const url = await http(`/api/payment/${invoiceId}`);
    const newTab = window.open(url, '_blank');
    if (newTab)
      newTab.focus();
  };

  const { mutate: loadManageSubUrl } = useSubscriptionManageUrl(() => {
    toast.error("Failed to load subscription management URL.");
  });

  const hasPaymentHistory = data && data.length > 0;
  const shouldShowPaymentOptions = hasPaymentHistory && !isInHQ && !isFreePlan && SubscriptionPlanName.Starter;

  return <Card sx={styles.card}>
    <CardHeader title="Payment History" />
    <CardContent sx={styles.cardContent}>
      <SpinnerWrapper loading={isLoading}>
        <List sx={styles.list}>
          {data?.map(({ amount, date, failedMessage, invoiceId, success }, i) => (
            <Box key={i}>
              <ListItem>
                <ListItemAvatar>
                  {success ? <CheckCircleOutlineIcon color="success" /> : <ErrorOutlineIcon color="error" />}
                </ListItemAvatar>
                <Typography sx={styles.datum}>{formatDate(date, "MM/dd/yy")}</Typography>
                <Typography sx={styles.datum}>{formatCurrency(amount)}</Typography>
                {success ? <ListItemText primary={invoiceId ? (
                  // eslint-disable-next-line jsx-a11y/anchor-is-valid
                  <a onClick={async () => await getInvoiceUrl(invoiceId)} style={styles.link} target="_blank">View Invoice</a>
                ) : (
                  "Payment"
                )} /> : <ListItemText primary={failedMessage} sx={{ color: theme.palette.error.main }} />}
              </ListItem>
              <Divider />
            </Box>
          ))}
          {
            data && data?.length === 0 && <Typography variant="h6">No payment history yet.</Typography>
          }
        </List>
      </SpinnerWrapper>
    </CardContent>
    {shouldShowPaymentOptions && (
      <CardActions sx={styles.cardActions}>
        <Button
          variant="contained"
          onClick={() => loadManageSubUrl()}
          startIcon={<SettingsIcon />}
        >
          Payment Options
        </Button>
      </CardActions>
    )}
  </Card>;
}

const styles = {
  card: {
    gridColumn: "span 6",
    gridRow: "span 9",
    display: "flex",
    flexDirection: "column"
  },
  cardContent: {
    overflow: "auto",
    flexGrow: 1
  },
  list: {
    height: "100%",
    overflow: "auto"
  },
  datum: {
    marginRight: "20px"
  },
  link: {
    cursor: "pointer",
    textDecoration: "underline",
    color: theme.palette.primary.main
  },
  cardActions: {
    display: "flex",
    justifyContent: "center",
    paddingBottom: "24px"
  }
}
